import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import toast from "react-hot-toast";

import type { FCWithChildren } from "koljobs-types";

import { BusinessEventEnum } from "@/consts/AnalyticsEvent";
import { Button } from "@/features/common/components/Button";
import {
  useConfirmDialogContext,
  withConfirmDialog,
} from "@/features/common/contexts/useConfirmDialogContext";
import { useDialogContext } from "@/features/common/contexts/useDialogContext";
import { useAnalytics } from "@/features/common/hooks/useAnalytics";
import { useErrorHandling } from "@/features/common/hooks/useErrorHandling";
import { useInvalidateQueries } from "@/features/common/hooks/useInvalidateQueries";
import { getInviteModelFormYupResolver } from "@/features/job/yup/inviteModelFormYupResolver";
import {
  GenderEnum,
  useCreateJobApplicationMutation,
} from "@/generated/graphql-hooks";

export type InviteModelInputs = {
  modelUserIds: string[];
};

export const InviteModelForm: FCWithChildren<{
  jobId: string;
  invitationsCount: number;
  tab: GenderEnum;
}> = withConfirmDialog(({ children, jobId, invitationsCount, tab }) => {
  const { trackEvent } = useAnalytics();
  const { openConfirmDialog, closeConfirmDialog } = useConfirmDialogContext();
  const { closeDialog } = useDialogContext();
  const { handleGraphqlError } = useErrorHandling();
  const form = useForm<InviteModelInputs>({
    mode: "onChange",
    resolver: getInviteModelFormYupResolver(invitationsCount),
  });
  const { mutateAsync: createJobApplication, isLoading } =
    useCreateJobApplicationMutation();
  const { invalidateQueries } = useInvalidateQueries();

  const onInviteModels = async ({ modelUserIds }: InviteModelInputs) => {
    if (!modelUserIds?.length) return;

    try {
      await Promise.all(
        modelUserIds.map(async (modelUserId) => {
          await createJobApplication({
            jobId,
            modelUserId,
          });
        }),
      );
      invalidateQueries([
        "JobsByBusiness.infinite",
        "Models.infinite",
        "ModelGendersCount",
      ]);
      trackEvent(BusinessEventEnum.INVITE_JOB);
      closeDialog();
      toast.success("已成功發出邀請");
    } catch (error) {
      handleGraphqlError(error, {
        tags: {
          action: "SelectModelForm.onInviteModels",
        },
      });
    }
  };

  const selectedModelCount = form.watch("modelUserIds")?.length;

  useEffect(() => {
    form.reset({});
  }, [tab]);

  return (
    <FormProvider {...form}>
      <form
        onSubmit={form.handleSubmit(onInviteModels)}
        className="flex flex-1 flex-col justify-between"
      >
        <div className="flex h-full flex-col space-y-4 overflow-y-auto px-4 pt-4 sm:max-h-[30rem] sm:px-6 sm:pb-10">
          {children}
        </div>
        <div className="flex justify-end space-x-4 bg-white p-4 sm:!-mb-6 sm:rounded-b-md">
          <div>
            <Button
              variant="outline"
              className="px-6 py-2"
              onClick={closeDialog}
            >
              返回
            </Button>
          </div>
          <div>
            <Button
              type="button"
              className="!px-6"
              disabled={!form.formState.isValid}
              disabledColor="primary"
              isLoading={isLoading || form.formState.isSubmitting}
              onClick={() =>
                openConfirmDialog({
                  content: "發送邀請後，KOL將會收到你的邀請通知。",
                  onConfirm: () => {
                    closeConfirmDialog();
                    form.handleSubmit(onInviteModels)();
                  },
                })
              }
            >
              發送邀請 ({selectedModelCount ?? 0})
            </Button>
          </div>
        </div>
      </form>
    </FormProvider>
  );
});
